import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { useDateFormatter } from './useDateFormatter';
import type { Agenda_AlterarCompromissoResponse } from '../contracts/Agenda_AlterarCompromissoResponse';
import '../contracts/Agenda_AgendarCompromissoInput';
import '../contracts/Agenda_BuscarCompromissosInput.ts';
import '../contracts/Agenda_AlterarCompromissoInput.ts';
import '../contracts/Agenda_BuscarFormasPagamentoInput';
import '../contracts/Agenda_BuscarOrcamentosInput';
import '../contracts/Agenda_AgendarCompromissoResponse';
import '../contracts/Agenda_AlterarCompromissoResponse';
import '../contracts/Agenda_BuscarCompromissosResponse';
import '../contracts/Agenda_BuscarFormasPagamentoResponse';
import '../contracts/Agenda_BuscarOrcamentosResponse';

import type { Agenda_AgendarCompromissoInput } from '../contracts/Agenda_AgendarCompromissoInput';
import type { Agenda_AlterarCompromissoInput } from '../contracts/Agenda_AlterarCompromissoInput';
import type { Agenda_BuscarCompromissosInput } from '../contracts/Agenda_BuscarCompromissosInput';
/**
 * Composable for Agenda (Calendar/Schedule) operations
 * Provides methods to interact with the Agenda API
 */
export function useAgenda() {
  const isLoading = ref(false);
  const error = ref(null);
  const { localDateTimeToApi } = useDateFormatter();

  /**
   * Search budgets for scheduling
   * @param {Array} franquiasIds - Array of franchise IDs
   */
  const buscarOrcamentos = async (franquiasIds: any) => {
    log('useAgenda', 'buscarOrcamentos', 'Searching budgets', franquiasIds);
    isLoading.value = true;
    error.value = null;

    try {
      // Body is ignored by the endpoint (orcamentos are scoped by the
      // authenticated user's franchise permissions). franquiasIds is kept
      // on the function signature for back-compat but not sent.
      void franquiasIds;
      const response = await httpService.post('/index.php/agenda/buscar_orcamentos', null, {
        inputContract: 'Agenda_BuscarOrcamentosInput',
        responseContract: 'Agenda_BuscarOrcamentosResponse',
      });
      log('useAgenda', 'buscarOrcamentos', 'Response', response);

      // Return full envelope; orcamentos live under response.dados.orcamentos per the
      // Agenda_BuscarOrcamentosResponse contract (canonical {success, dados, error}).
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao buscar orçamentos');
      }
    } catch (err) {
      log('useAgenda', 'buscarOrcamentos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Search appointments/compromissos
   * @param {String} userType - User type ('Operador' or '')
   * @param {Object} payload - Search payload
   */
  const buscarCompromissos = async (userType: any, payload: Agenda_BuscarCompromissosInput) => {
    log('useAgenda', 'buscarCompromissos', 'Searching appointments', { userType, payload });
    isLoading.value = true;
    error.value = null;

    try {
      const endpoint = `/index.php/agenda/buscar_compromissos/${userType || ''}`;
      validate('Agenda_BuscarCompromissosInput', payload);
      const response = await httpService.post(endpoint, payload, {
        responseContract: 'Agenda_BuscarCompromissosResponse'
      });
      log('useAgenda', 'buscarCompromissos', 'Response', response);

      // Return full response object so callers can access response.compromissos and response.bloqueios
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao buscar compromissos');
      }
    } catch (err) {
      log('useAgenda', 'buscarCompromissos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update/alter a compromisso
   * @param {Object} data - Update data
   */
  const alterarCompromisso = async (input: Agenda_AlterarCompromissoInput) => {
    log('useAgenda', 'alterarCompromisso', 'Updating compromisso', input);
    isLoading.value = true;
    error.value = null;

    try {
      // App → API boundary: browser-local datetime → ISO 8601 UTC
      const data: Agenda_AlterarCompromissoInput = {
        ...input,
        compromisso_data_hora_inicio: input.compromisso_data_hora_inicio == null ? input.compromisso_data_hora_inicio : localDateTimeToApi(input.compromisso_data_hora_inicio),
        compromisso_data_hora_fim: input.compromisso_data_hora_fim == null ? input.compromisso_data_hora_fim : localDateTimeToApi(input.compromisso_data_hora_fim),
      };
      validate('Agenda_AlterarCompromissoInput', data);
      const response = await httpService.post<Agenda_AlterarCompromissoResponse>(
        '/index.php/agenda/alterar_compromisso',
        data,
        { responseContract: 'Agenda_AlterarCompromissoResponse' }
      );
      log('useAgenda', 'alterarCompromisso', 'Response', response);

      // Return full response object for consistency
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao alterar compromisso');
      }
    } catch (err) {
      log('useAgenda', 'alterarCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Search payment methods
   */
  const buscarFormasPagamento = async () => {
    log('useAgenda', 'buscarFormasPagamento', 'Searching payment methods');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/agenda/buscar_formas_pagamento', null, {
        inputContract: 'Agenda_BuscarFormasPagamentoInput',
        responseContract: 'Agenda_BuscarFormasPagamentoResponse'
      });
      log('useAgenda', 'buscarFormasPagamento', 'Response', response);

      // Return full response object so callers can access response.success and response.dados.formas_pagamento
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao buscar formas de pagamento');
      }
    } catch (err) {
      log('useAgenda', 'buscarFormasPagamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Schedule/create a new compromisso
   * @param {Object} data - Compromisso data
   */
  const agendarCompromisso = async (input: Agenda_AgendarCompromissoInput) => {
    log('useAgenda', 'agendarCompromisso', 'Scheduling compromisso', input);
    isLoading.value = true;
    error.value = null;

    try {
      // App → API boundary: browser-local datetime → ISO 8601 UTC
      const data: Agenda_AgendarCompromissoInput = {
        ...input,
        compromisso_data_hora_inicio: input.compromisso_data_hora_inicio == null ? input.compromisso_data_hora_inicio : localDateTimeToApi(input.compromisso_data_hora_inicio),
        compromisso_data_hora_fim: input.compromisso_data_hora_fim == null ? input.compromisso_data_hora_fim : localDateTimeToApi(input.compromisso_data_hora_fim),
      };
      validate('Agenda_AgendarCompromissoInput', data);
      const response = await httpService.post('/index.php/agenda/agendar_compromisso', data, {
        responseContract: 'Agenda_AgendarCompromissoResponse',
      });
      log('useAgenda', 'agendarCompromisso', 'Response', response);

      // Return full response object so callers can access response.success and response.compromisso_id
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao agendar compromisso');
      }
    } catch (err) {
      log('useAgenda', 'agendarCompromisso', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    buscarOrcamentos,
    buscarCompromissos,
    alterarCompromisso,
    buscarFormasPagamento,
    agendarCompromisso
  };
}
